import React from 'react';
import {
  Avatar,
  Badge,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import './styles.css';
import { isMobile } from 'react-device-detect';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { isGif, transformCloudinaryGifToStill } from '../utils';
import { getAvatarSrc, resizeImage } from '../../../api/cloudinary';
import { Penalty, Team } from '@par/shared/types/game';
import { Player } from '../types';
import { Mode } from '@par/shared/common/types';

type PropTypes = {
  avatarPublicId?: string;
  avatarUrl?: string;
  correctAnswer?: boolean;
  currentScore?: number;
  disconnected?: boolean;
  ghostChallenge?: boolean;
  matchMaking?: boolean;
  mode?: Mode;
  parScore?: number;
  penalty?: Penalty;
  players?: Record<string, Player>;
  teamMembers?: string[];
  timerLimitReached: boolean;
};

export const Players = ({
  avatarPublicId,
  avatarUrl,
  correctAnswer,
  currentScore,
  disconnected,
  ghostChallenge,
  matchMaking,
  mode,
  penalty,
  players,
  teamMembers,
  timerLimitReached,
}: PropTypes) => {
  const theme = useTheme();

  const avatarBorderColor = () => {
    if (penalty?.penaltyTimestamp) return 'warning.main';
    if (correctAnswer) return 'success.main';
    return 'error.main';
  };

  const avatarSize = (numOfPlayers: number) => {
    if (numOfPlayers === 1) {
      return 110;
    } else if (numOfPlayers === 2) {
      return 90;
    } else if (numOfPlayers === 3) {
      return 75;
    } else if (numOfPlayers >= 4) {
      return 60;
    }
  };

  const getAvatar = () => {
    if (mode === 'teams' && players && teamMembers) {
      const members = Object.values(players).filter((p) =>
        teamMembers.includes(p.profileId)
      );
      return (
        <Stack
          spacing={1}
          width={'100%'}
          height={'100%'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Stack direction={'row'} height={'100%'} spacing={1}>
            {members.map((m) => (
              <Avatar
                src={getAvatarSrc(m.avatarPublicId, m.avatarUrl, 200, 200)}
                variant="circular"
                sx={{
                  height: avatarSize(teamMembers.length),
                  width: avatarSize(teamMembers.length),
                  border:
                    m.correctAnswer || timerLimitReached ? '5px solid' : 'none',
                  borderColor: avatarBorderColor(),
                  objectFit: 'cover',
                  opacity:
                    penalty?.penaltyTimestamp ||
                    (correctAnswer && currentScore !== undefined) ||
                    (!correctAnswer && timerLimitReached && currentScore) ||
                    (disconnected && !ghostChallenge && !matchMaking)
                      ? 0.2
                      : 1,
                }}
              />
            ))}
          </Stack>
        </Stack>
      );
    }
    return (
      <Avatar
        src={getAvatarSrc(avatarPublicId, avatarUrl, 200, 200)}
        variant="circular"
        sx={{
          height: 110,
          width: 110,
          border:
            correctAnswer || penalty?.penaltyTimestamp || timerLimitReached
              ? '5px solid'
              : 'none',
          borderColor: avatarBorderColor(),
          objectFit: 'cover',
          opacity:
            penalty?.penaltyTimestamp ||
            (correctAnswer && currentScore !== undefined) ||
            (!correctAnswer && timerLimitReached && currentScore) ||
            (disconnected && !ghostChallenge && !matchMaking)
              ? 0.2
              : 1,
        }}
      />
    );
  };

  return (
    <Stack alignItems="center" position={'relative'}>
      {/* <div className="profile-pic-cont"> */}
      <Badge
        badgeContent={
          isMobile ? (
            <PhoneAndroidIcon
              fontSize="small"
              sx={{
                background: theme.palette.secondary.main,
                padding: '3px',
                borderRadius: '4px',
                marginBottom: '12px',
                marginRight: '12px',
                filter:
                  correctAnswer ||
                  penalty?.penaltyTimestamp ||
                  timerLimitReached
                    ? 'brightness(0.5)'
                    : 'none',
              }}
            />
          ) : null
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {getAvatar()}
      </Badge>
      {correctAnswer && currentScore !== undefined ? (
        <Typography
          variant="h3"
          color={'success.main'}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            textShadow: `0 0 7px ${theme.palette.success.main}, 0 0 10px ${theme.palette.success.main}, 0 0 21px ${theme.palette.success.dark}, 0 0 42px ${theme.palette.success.dark}, 0 0 82px ${theme.palette.success.dark}, 0 0 92px ${theme.palette.success.dark}, 0 0 102px ${theme.palette.success.dark}, 0 0 151px ${theme.palette.success.dark}`,
          }}
        >
          {currentScore > 0 ? `+${currentScore}` : currentScore}
        </Typography>
      ) : null}
      {!correctAnswer && timerLimitReached ? (
        <Typography
          variant="h3"
          color={'error.main'}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#fff',
            textShadow: `0 0 7px ${theme.palette.error.main}, 0 0 10px ${theme.palette.error.main}, 0 0 21px ${theme.palette.error.dark}, 0 0 42px ${theme.palette.error.dark}, 0 0 82px ${theme.palette.error.dark}, 0 0 92px ${theme.palette.error.dark}, 0 0 102px ${theme.palette.error.dark}, 0 0 151px ${theme.palette.error.dark}`,
          }}
        >
          {currentScore > 0 ? `+${currentScore}` : currentScore}
        </Typography>
      ) : null}
      {penalty?.penaltyTimestamp ? (
        <CircularProgress
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'warning.main',
          }}
        />
      ) : null}
      {disconnected && !ghostChallenge && !matchMaking ? (
        <>
          <Typography sx={{ position: 'absolute' }}>DISCONNECTED</Typography>
          <PowerOffIcon
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 60,
              // color: 'warning.main',
            }}
          />
        </>
      ) : null}
    </Stack>
  );
};
